import type { StaffFeatureId } from "@wallyo/contracts/actions";
import type { FeePaymentDetail, PaymentAdjustment } from "@wallyo/contracts/console/payments";
import { t } from "../../i18n";
import { formatDateTime, formatMoney } from "../../i18n/format";
import { customerHref } from "../customers/customer-route";
import { canSettle, paymentActions, type PaymentActionKind } from "./payment-actions";
import { PAYMENT_STATUS_TONE, adjustmentState, paymentStatusName, providerName } from "./payment-format";
import { paymentsHref, type PaymentFilters } from "./payment-route";

export type PaymentOutcome = { text: string; tone: "ok" | "bad" | "warn" };

export type PaymentDetailViewProps = {
  payment: FeePaymentDetail;
  filters: PaymentFilters;
  features: readonly StaffFeatureId[];
  /** What the server said the last correction did. */
  outcome: PaymentOutcome | null;
  onAction: (kind: PaymentActionKind) => void;
  onSettle: (adjustment: PaymentAdjustment) => void;
  onDismissOutcome: () => void;
};

/** One fee payment: what was paid, its corrections, what can still be refunded, and the pro's credit. */
export function PaymentDetailView(props: PaymentDetailViewProps) {
  const { payment: p, filters, features } = props;
  const money = (amount: number) => formatMoney(amount, p.currency);
  const actions = paymentActions(p, features);
  return (
    <div className="card verif-detail payment-detail" data-payment={p.platformFeePaymentId}>
      <div className="verif-head">
        <div>
          <h3>
            {p.pro.name} · {money(p.amount)}
          </h3>
          <p className="meta">
            {[
              providerName(p.provider),
              t(`payments.feeType.${p.feeType}` as const),
              formatDateTime(p.createdAt),
              p.pro.maskedPhone,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
        <span className="verif-pills">
          <span className={`pill ${PAYMENT_STATUS_TONE[p.status]}`}>{paymentStatusName(p.status)}</span>
          <span className="meta mono" title={t("payments.detail.versionTitle")}>
            v{p.version}
          </span>
        </span>
      </div>

      {props.outcome ? (
        <div className={`banner ${props.outcome.tone === "ok" ? "ok" : "bad"}`} role="status" data-outcome="">
          <span>{props.outcome.text}</span>
          <button className="btn quiet small" type="button" onClick={props.onDismissOutcome}>
            {t("reports.dismiss")}
          </button>
        </div>
      ) : null}

      {p.likelyDuplicate ? (
        <div className="verif-banned" role="alert" data-duplicates="">
          <b>{t("payments.detail.duplicate")}</b>
          <span>{t("payments.detail.duplicateBody")}</span>
          <span>
            {p.duplicateOf.map((id) => (
              <a key={id} className="go-link mono" href={paymentsHref(filters, id)}>
                {id.slice(0, 8)}{" "}
              </a>
            ))}
          </span>
        </div>
      ) : null}

      <ul className="kv">
        <li>
          <span>{t("payments.detail.pro")}</span>
          <span>
            <a className="go-link" href={customerHref(p.pro.userId)}>
              {p.pro.name}
            </a>{" "}
            <a className="go-link meta" href={paymentsHref({ ...filters, workerId: p.pro.workerId })}>
              {t("payments.detail.proPayments")}
            </a>
          </span>
        </li>
        <li>
          <span>{t("payments.detail.transaction")}</span>
          <span className={p.providerTransactionId ? "mono" : undefined}>
            {p.providerTransactionId ?? t("reports.detail.none")}
          </span>
        </li>
        {p.jobId ? (
          <li>
            <span>{t("payments.detail.job")}</span>
            <span className="mono">{p.jobId}</span>
          </li>
        ) : null}
        <li>
          <span>{t("payments.detail.refunded")}</span>
          <span data-refunded="">{money(p.refundedAmount)}</span>
        </li>
        <li>
          <span>{t("payments.detail.refundable")}</span>
          <span data-refundable="">{money(p.refundableAmount)}</span>
        </li>
        <li>
          <span>{t("payments.detail.credit")}</span>
          <span data-credit="">{money(p.proCreditBalance)}</span>
        </li>
      </ul>

      <h2 className="verif-h">{t("payments.detail.adjustments")}</h2>
      {p.adjustments.length === 0 ? (
        <p className="foot">{t("payments.detail.noAdjustments")}</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t("payments.col.kind")}</th>
                <th className="num">{t("payments.col.amount")}</th>
                <th>{t("payments.col.status")}</th>
                <th>{t("payments.col.by")}</th>
                <th aria-label={t("payments.col.actions")} />
              </tr>
            </thead>
            <tbody>
              {p.adjustments.map((adjustment) => {
                const state = adjustmentState(adjustment);
                return (
                  <tr key={adjustment.paymentAdjustmentId} data-adjustment={adjustment.paymentAdjustmentId}>
                    <td>
                      {t(`payments.kind.${adjustment.kind}` as const)}
                      <br />
                      <span className="meta">{adjustment.reason}</span>
                      {adjustment.providerReference ? (
                        <span className="meta mono">
                          <br />
                          {adjustment.providerReference}
                        </span>
                      ) : null}
                    </td>
                    <td className="num">{formatMoney(adjustment.amount, adjustment.currency)}</td>
                    <td>
                      <span className={`pill ${state.tone}`} data-state={adjustment.status}>
                        {state.label}
                      </span>
                      {state.hint ? (
                        <span className="meta">
                          <br />
                          {state.hint}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {adjustment.staff.name}
                      <br />
                      <span className="meta">{formatDateTime(adjustment.createdAt)}</span>
                    </td>
                    <td className="row-actions">
                      {canSettle(adjustment, features) ? (
                        <button
                          className="btn small"
                          type="button"
                          data-settle={adjustment.paymentAdjustmentId}
                          onClick={() => props.onSettle(adjustment)}
                        >
                          {t("payments.action.settle")}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {actions.length > 0 ? (
        <div className="case-actions">
          <h2 className="verif-h">{t("payments.detail.actions")}</h2>
          <div className="btns">
            {actions.map((action) => (
              <button
                key={action.kind}
                className="btn"
                type="button"
                disabled={!action.allowed}
                data-action={action.kind}
                onClick={() => props.onAction(action.kind)}
              >
                {t(`payments.action.${action.kind}` as const)}
              </button>
            ))}
          </div>
          <p className="foot">{t("payments.detail.neverOptimistic")}</p>
        </div>
      ) : null}
    </div>
  );
}
